import {
  CheckCircle2,
  ChevronRight,
  Clock,
  Download,
  RefreshCw,
  RotateCcw,
  Search,
  XCircle,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { OperationType, TransactionStatus, type CardTransaction } from "../types/card-movements.types";
import { formatMovementDateTime, visibleCardMovements } from "../utils/cardMovementHelpers";
import { getTransactionTypeMeta } from "../utils/cardTransactionDetailHelpers";
import {
  formatPlainAmount,
  formatSignedAmount,
  resolveUsdAmount,
  shouldShowLocalAmount,
} from "../utils/transactionAmountFormat";

type CardMovementsProps = {
  movements: CardTransaction[];
  isLoading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  onRowClick?: (transactionId: string) => void;
  onRefresh?: () => void;
};

const STATUS_META: Record<
  TransactionStatus,
  {
    variant: "success" | "warning" | "destructive" | "secondary";
    icon: LucideIcon;
    tKey: string;
  }
> = {
  COMPLETED: {
    variant: "success",
    icon: CheckCircle2,
    tKey: "movements_status_completed",
  },
  PENDING: {
    variant: "warning",
    icon: Clock,
    tKey: "movements_status_pending",
  },
  FAILED: {
    variant: "destructive",
    icon: XCircle,
    tKey: "movements_status_failed",
  },
  REVERSED: {
    variant: "secondary",
    icon: RotateCcw,
    tKey: "movements_status_reversed",
  },
};

export const CardMovements = ({
  movements,
  isLoading = false,
  onLoadMore,
  hasMore = false,
  onRowClick,
  onRefresh,
}: CardMovementsProps) => {
  const { t } = useTranslations("cards");

  // Derived, not stateful: `CardDetailPage` paginates with `movements.length` over the unfiltered
  // list, so dropping rows here leaves the `skip` cursor agreeing with the backend.
  const rows = useMemo(() => visibleCardMovements(movements), [movements]);

  return (
    // Laid out against its own width (`@container`): next to the 380px sidebar this column is narrow on a
    // laptop too, not only on a phone, and the fixed status and amount columns left the merchant no room.
    <section className="@container overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      {/* Header: search + export */}
      <div className="flex flex-col gap-2 border-b border-border p-3 @md:flex-row @md:items-center @md:justify-between @md:gap-3">
        <div className="relative w-full @md:max-w-xs @md:flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            disabled
            placeholder={t("filters_search_alias_placeholder")}
            className="h-8 w-full rounded-[var(--radius-md)] border border-border bg-background pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
          />
        </div>
        <div className="flex items-center gap-2">
          {onRefresh && (
            <ColocaButton
              variant="default"
              size="sm"
              icon={RefreshCw}
              onClick={onRefresh}
              loading={isLoading}
            >
              {t("movements_refresh")}
            </ColocaButton>
          )}
          <ColocaButton variant="default" size="sm" icon={Download} disabled>
            {t("card_detail_export")}
          </ColocaButton>
        </div>
      </div>

      {/* List */}
      <div className="divide-y divide-border">
        {isLoading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-3">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              <span className="text-sm text-muted-foreground">{t("movements_loading")}</span>
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">{t("movements_empty")}</div>
        ) : (
          rows.map((tx) => {
            const meta = getTransactionTypeMeta(tx.transaction_type, tx.top_up_source);
            const Icon = meta.icon;
            const statusMeta = STATUS_META[tx.status];
            const StatusIcon = statusMeta.icon;
            const merchant = tx.merchant_name || tx.description || "—";

            const usdAmount = resolveUsdAmount(tx);
            const showLocal = shouldShowLocalAmount(tx);

            return (
              <button
                key={tx.id}
                type="button"
                onClick={() => onRowClick?.(tx.id)}
                className="flex w-full items-center gap-3 p-3.5 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span
                  className={cn(
                    "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--radius-md)]",
                    meta.iconWrapper,
                  )}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-foreground">{merchant}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {formatMovementDateTime(tx.created_at)}
                  </div>
                  <div className="mt-1 @lg:hidden">
                    <ColocaBadge variant={statusMeta.variant} icon={StatusIcon}>
                      {t(statusMeta.tKey as never)}
                    </ColocaBadge>
                  </div>
                </div>
                <div className="hidden w-28 shrink-0 justify-start @lg:flex">
                  <ColocaBadge variant={statusMeta.variant} icon={StatusIcon}>
                    {t(statusMeta.tKey as never)}
                  </ColocaBadge>
                </div>
                <div className="flex shrink-0 flex-col items-end text-right @lg:w-32">
                  <span
                    className={cn(
                      "inline-flex items-baseline gap-1 font-mono text-sm font-medium tabular-nums",
                      tx.operation_type === OperationType.CREDIT
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-foreground",
                      tx.status === TransactionStatus.FAILED && "text-muted-foreground line-through",
                    )}
                  >
                    {usdAmount != null
                      ? formatSignedAmount(usdAmount, "USD", tx.operation_type)
                      : formatSignedAmount(tx.amount, tx.currency, tx.operation_type)}
                    <span className="text-[10px] font-normal text-muted-foreground">USD</span>
                  </span>
                  {showLocal && (
                    <span className="font-mono text-[10px] tabular-nums text-muted-foreground">
                      {formatPlainAmount(tx.amount, tx.currency)}
                    </span>
                  )}
                </div>
                <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              </button>
            );
          })
        )}
      </div>

      {/* Load more */}
      {hasMore && !isLoading && onLoadMore && (
        <div className="border-t border-border p-4 text-center">
          <button
            type="button"
            onClick={onLoadMore}
            className="text-sm font-medium text-primary hover:underline"
          >
            {t("movements_load_more")}
          </button>
        </div>
      )}

      {isLoading && rows.length > 0 && (
        <div className="border-t border-border p-4 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <span>{t("movements_loading_more")}</span>
          </div>
        </div>
      )}
    </section>
  );
};
